import React, { useEffect, useState } from "react";
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Alert, Button, FormGroup, Label, Input, Row, Col, Spinner,
} from "reactstrap";
import { createUser, getKullaniciDili, getUserGroups, sendAktivasyonMaili, updateUser } from "../../../services/userService";
import { useYetki, PERMISSION_KEYS } from "../../../services/rbacService";
import { getRoller, PORTAL_IZNI } from "../../../services/portalRbacService";
import FirmaSearchSelect from "../../../Components/Firma/FirmaSearchSelect";
import TelefonGirdisi from "../../../Components/Common/TelefonGirdisi";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

const KOK_ROL_ID = 1;

/**
 * UserFormModal — kullanıcı oluştur / düzenle (backlog #545, 2026-10-26).
 *
 * WAS (#379, 2026-10-13): yalnızca ünvan + uyruk düzenlenebiliyordu, oluşturma yoktu; canlı giriş kullanıcı adını, parolayı, adı,
 * soyadı, e-postayı, telefonu ve aktifliği her girişte PHP'den eziyor, sunucuda POST /users reddediliyordu. Giriş artık kendi
 * kullanıcı tablomuzu okuyor (#523) ve POST /users açık (#524), bu yüzden form tam alan kümesiyle döndü:
 *   · mode="create" — kullanıcı adı, parola, ad, soyad, ünvan, telefon, e-posta, uyruk, rol → POST /users?userGroupId=
 *   · mode="edit"   — aynı alanlar (parola boş = değişmez; rol Rol sekmesinden değişir) + durum → PUT /users/update/{id}; YALNIZCA
 *     değişen alan gönderilir (`updateUser` kaydı okuyup üstüne yazar, bkz. userService.js)
 * Backend reddi olduğu gibi gösterilir: SuperAdmin 403, rbac.manage'siz personel rolü 403, kullanıcı adı başkasında 409 (#544),
 * boş ad / parola 400, kendini pasifleştirme 409, kökü başkasının düzenlemesi 403.
 *
 * #628 (2026-10-30): oluşturma artık MANAGE_USER_ROLES YA DA kullanici.olustur ister (Kayıt ve Saha rolleri); yalnızca kullanici.olustur
 * olan kişi personel rolü veremez (rol listesi firma tarafı rollerle sınırlı). Firma tarafı rolde isteğe bağlı firma seçilir
 * (POST /users?firmaId=, aynı işlemde bağlanır) ya da `firma` ile sabitlenir (Firma Detay). Parola ZORUNLU DEĞİL: boşsa kullanıcı
 * giriş yapamaz, parolayı aktivasyon mailindeki bağlantıdan kendisi belirler; e-posta varsa "Aktivasyon maili gönder" (açık) oluşturmadan
 * hemen sonra POST /users/{id}/aktivasyon-maili çağırır ve penceredeki sonuç mesajı gidip gitmediğini söyler (gitmeyen mail kullanıcıyı
 * geri almaz).
 * #611 (backend #610): DİL seçici (Otomatik = hiç yazılmaz, uyruktan / firmadan türetilir · Türkçe · English) hem oluşturmada (`languageId` gövdede)
 * hem düzenlemede (kayıtlı dil GET /users/{id}'den okunur, YALNIZCA değişirse gönderilir; Otomatik = null) vardır; aktivasyon maili bu dilde gider.
 * Firma seçiliyken (ya da sabitken) ve çağıranda portal.kullanici_rol.manage varsa PORTAL ROLLERİ işaret kutularıyla seçilir (`portalRolIdleri`);
 * hiçbiri seçilmezse firma tarafı rolün varsayılan portal rolü verilir. Mail hâlâ oluşturmadan sonra ayrı çağrıyla gider (backend'in
 * `aktivasyonMaili=true` kısayolu kullanılmaz: kişiye bağlama `sonrasi` önce çalışabilsin ve eski backend'le de çalışsın).
 * Düzenleme ve parola atama MANAGE_USER_ROLES ister: bu pencereyi AÇAN taraf yetkiyi denetler (yetkisiz kullanıcıya düğme hiç çizilmez).
 * Üst bileşen pencereyi yalnızca açıkken bağlar (`{acik && <UserFormModal />}`): alanlar mount'ta kullanıcının güncel değerleriyle başlar.
 *
 * Props:
 *   mode       : "create" | "edit"
 *   user       : (edit) `kullanici-listesi` satırı
 *   varsayilan : (create) { ad, soyad, telefon, eposta } — Kişi Kartı › Kullanıcı Oluştur kişinin bilgisini önceden doldurur
 *   firma      : (create) { id, ad } — verilirse yeni giriş bu firmaya bağlanır, firma değiştirilemez (Firma Detay › Yeni kullanıcı oluştur)
 *   sonrasi    : (create) async (userId) — oluşturmadan hemen sonra çalışır (ör. kişiye bağlama). Hata verirse kullanıcı YİNE DE
 *                oluşmuştur: pencere bunu söyler, "Kapat" sonuçla (`baglanamadi`) kapanır ki üst sayfa yeniden okusun.
 *   onClose    : fn(sonuc) — iptalde null; başarıda { userId, kullaniciAdi, uyarilar[], olustu, baglanamadi?, mail? }
 */
const UserFormModal = ({ mode = "edit", user, varsayilan, firma: sabitFirma, sonrasi, onClose }) => {
  const { t } = useTranslation("yonetim");
  const olustur = mode === "create";
  // #628: personel rolü vermek MANAGE_USER_ROLES ister (kullanici.olustur yalnızca firma tarafı rolleri açar); null = henüz bilinmiyor.
  const yonetici = useYetki(PERMISSION_KEYS.MANAGE_USER_ROLES);
  // #611: portal rolü vermek portal.kullanici_rol.manage ister (yalnızca Admin); yoksa seçici çizilmez, giriş varsayılan portal rolünü alır.
  const portalYonetici = useYetki(PORTAL_IZNI.KULLANICI_ROL);

  const ilk = {
    ad: olustur ? (varsayilan?.ad || "") : (user?.userName || ""),
    soyad: olustur ? (varsayilan?.soyad || "") : (user?.userSurname || ""),
    unvan: olustur ? "" : (user?.userTitle || ""),
    telefon: olustur ? (varsayilan?.telefon || "") : (user?.userPhone || ""),
    eposta: olustur ? (varsayilan?.eposta || "") : (user?.userEmail || ""),
    kullaniciAdi: olustur ? "" : (user?.userLoginName || ""),
    uyruk: olustur ? "1" : String(user?.uyruk?.kullaniciUyrukId ?? 1),
    aktif: olustur ? "1" : (Number(user?.userActive) === 1 || user?.userActive === true ? "1" : "0"),
  };

  const [kullaniciAdi, setKullaniciAdi] = useState(ilk.kullaniciAdi);
  const [sifre, setSifre] = useState("");
  const [ad, setAd] = useState(ilk.ad);
  const [soyad, setSoyad] = useState(ilk.soyad);
  const [unvan, setUnvan] = useState(ilk.unvan);
  const [telefon, setTelefon] = useState(ilk.telefon);
  const [eposta, setEposta] = useState(ilk.eposta);
  const [uyruk, setUyruk] = useState(ilk.uyruk);
  const [aktif, setAktif] = useState(ilk.aktif);
  const [rolId, setRolId] = useState("");
  const [roller, setRoller] = useState(null);
  const [firma, setFirma] = useState(null);
  const [postaGonder, setPostaGonder] = useState(true);
  // #611: "" = seçilmedi (türetilir), "1" Türkçe, "2" English. Düzenlemede kayıtlı değer okunana dek `dilIlk` null ve seçici kapalıdır.
  const [dil, setDil] = useState("");
  const [dilIlk, setDilIlk] = useState(null);
  const [portalRolleri, setPortalRolleri] = useState(null);
  const [portalHata, setPortalHata] = useState(false);
  const [portalSecim, setPortalSecim] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // #628: oluşturma bitti ve aktivasyon maili denendi: form kilitlenir, sonuç gösterilir, "Kapat" sonuçla kapanır.
  const [bitis, setBitis] = useState(null);
  // Oluşturma başarılı ama `sonrasi` (kişiye bağlama) hata verdi: kullanıcı var, form kilitlenir.
  const [olusan, setOlusan] = useState(null);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    if (!olustur) return undefined;
    let ignore = false;
    async function run() {
      try {
        const g = await getUserGroups();
        if (!ignore) {
          setRoller((g || [])
            .filter((x) => x.id !== KOK_ROL_ID)
            .map((x) => ({ id: x.id, ad: x.userGroupName, staff: Boolean(x.staff) }))
            .sort((a, b) => String(a.ad).localeCompare(String(b.ad))));
        }
      } catch (e) {
        if (!ignore) { setRoller([]); setError(e.message || i18n.t("yonetim:userFormModal.rollerOkunamadi")); }
      }
    }
    run();
    return () => { ignore = true; };
  }, [olustur]);

  // #611 (düzenleme): kayıtlı dil kart satırında yok, GET /users/{id}'den okunur. Okunamazsa seçici kapalı kalır ve dil kayıtta gönderilmez.
  useEffect(() => {
    if (olustur || !user?.userId) return undefined;
    let ignore = false;
    async function run() {
      try {
        const kayitli = await getKullaniciDili(user.userId);
        if (!ignore) { const v = kayitli == null ? "" : String(kayitli); setDil(v); setDilIlk(v); }
      } catch {
        // dil okunamadı: seçici kapalı kalır
      }
    }
    run();
    return () => { ignore = true; };
  }, [olustur, user?.userId]);

  // Personel rolleri yalnızca MANAGE_USER_ROLES sahibine ve firması sabit değilken sunulur (backend de aynısını ister).
  const gorunenRoller = (roller || []).filter((r) => !r.staff || (yonetici === true && !sabitFirma));
  const hedefRol = gorunenRoller.find((r) => String(r.id) === String(rolId));
  const firmaGerekir = olustur && hedefRol && !hedefRol.staff;
  // #611: portal rolleri firma belli olunca sunulur (roller firmadan bağımsız bir katalogdur, bir kez okunur).
  const seciliFirmaId = firmaGerekir ? (sabitFirma?.id ?? firma?.firmaId ?? null) : null;
  const portalGoster = Boolean(seciliFirmaId) && portalYonetici === true;
  useEffect(() => {
    if (!portalGoster || portalRolleri !== null) return undefined;
    let ignore = false;
    async function run() {
      try {
        const d = await getRoller();
        if (!ignore) setPortalRolleri((d?.roller || []).filter((r) => r.aktif !== false));
      } catch {
        if (!ignore) { setPortalRolleri([]); setPortalHata(true); }
      }
    }
    run();
    return () => { ignore = true; };
  }, [portalGoster, portalRolleri]);
  const kilitli = saving || olusan != null || bitis != null;
  const sifreBos = !sifre.trim();
  const epostaVar = Boolean(eposta.trim());

  const kapat = () => {
    if (saving) return;
    if (bitis) { onClose(bitis.sonuc); return; }
    onClose(olusan ? { ...olusan, baglanamadi: true } : null);
  };

  const kaydet = async () => {
    setError(null);
    const adiKirpik = kullaniciAdi.trim();
    if (!adiKirpik) { setError(t("userFormModal.kullaniciAdiZorunlu")); return; }
    // WAS (until #628): if (olustur && !sifre.trim()) { setError(t("userFormModal.sifreZorunlu")); return; }
    if (olustur && !rolId) { setError(t("userFormModal.rolZorunlu")); return; }

    if (!olustur) {
      const fields = {};
      if (adiKirpik !== ilk.kullaniciAdi.trim()) fields.userLoginName = adiKirpik;
      if (ad.trim() !== ilk.ad.trim()) fields.userName = ad.trim();
      if (soyad.trim() !== ilk.soyad.trim()) fields.userSurname = soyad.trim();
      if (unvan.trim() !== ilk.unvan.trim()) fields.userTitle = unvan.trim();
      if (telefon.trim() !== ilk.telefon.trim()) fields.userPhone = telefon.trim();
      if (eposta.trim() !== ilk.eposta.trim()) fields.userEmail = eposta.trim();
      if (uyruk !== ilk.uyruk) fields.userNationalityId = Number(uyruk);
      if (aktif !== ilk.aktif) fields.userActive = Number(aktif);
      if (dilIlk !== null && dil !== dilIlk) fields.languageId = dil === "" ? null : Number(dil);
      if (sifre.trim()) fields.userLoginPassword = sifre;
      if (Object.keys(fields).length === 0) { onClose(null); return; }
      setSaving(true);
      try {
        const kayit = await updateUser(user.userId, fields);
        onClose({ userId: user.userId, uyarilar: kayit?.uyarilar || [], olustu: false });
      } catch (e) {
        setError(e.message || t("userFormModal.kaydedilirkenHataOlustu"));
        setSaving(false);
      }
      return;
    }

    setSaving(true);
    let yeni;
    try {
      yeni = await createUser({
        userLoginName: adiKirpik,
        userLoginPassword: sifre,
        userName: ad.trim(),
        userSurname: soyad.trim(),
        userTitle: unvan.trim(),
        userPhone: telefon.trim(),
        userEmail: eposta.trim(),
        userNationalityId: Number(uyruk),
        ...(dil ? { languageId: Number(dil) } : {}),
        ...(portalGoster && portalSecim.size > 0 ? { portalRolIdleri: [...portalSecim] } : {}),
        // WAS (until #628): }, Number(rolId));
      }, Number(rolId), firmaGerekir ? (sabitFirma?.id ?? firma?.firmaId ?? null) : null);
    } catch (e) {
      setError(e.message || t("userFormModal.kaydedilirkenHataOlustu"));
      setSaving(false);
      return;
    }
    const sonuc = { userId: yeni.userId, kullaniciAdi: adiKirpik, uyarilar: [], olustu: true };
    if (sonrasi) {
      try {
        await sonrasi(yeni.userId);
      } catch (e) {
        setOlusan(sonuc);
        setError(t("userFormModal.olusturulduAmaBaglanamadi", { kullaniciAdi: adiKirpik, no: yeni.userId, hata: e.message || "" }));
        setSaving(false);
        return;
      }
    }
    // #628: parolasız yeni giriş + e-posta: aktivasyon maili hemen gider. Gitmeyen mail kullanıcıyı geri almaz; sonuç pencerede söylenir.
    if (sifreBos && epostaVar && postaGonder) {
      let mail;
      try {
        mail = await sendAktivasyonMaili(yeni.userId);
      } catch (e) {
        mail = { sent: false, error: e.message };
      }
      setBitis({
        sonuc: { ...sonuc, mail },
        gitti: Boolean(mail?.sent),
        mesaj: mail?.sent
          ? t("userFormModal.postaGitti", { adres: mail.to || eposta.trim() })
          : t("userFormModal.postaGitmedi", { neden: mail?.error || mail?.message || mail?.status || "" }),
      });
      setError(null);
      setSaving(false);
      return;
    }
    onClose(sonuc);
  };

  return (
    <Modal isOpen toggle={kapat} size="lg">
      <ModalHeader toggle={kapat}>{olustur ? t("userFormModal.baslikOlustur") : t("userFormModal.baslikDuzenle")}</ModalHeader>
      <ModalBody>
        {error && <Alert fade={false} color={olusan ? "warning" : "danger"} className="py-2 small">{error}</Alert>}
        {bitis && (
          <Alert fade={false} color={bitis.gitti ? "success" : "warning"} className="py-2 small" data-testid="uf-posta-sonucu">
            {t("userFormModal.olusturuldu", { kullaniciAdi: kullaniciAdi.trim() })} {bitis.mesaj}
          </Alert>
        )}
        <Row>
          <Col md="6">
            <FormGroup>
              <Label for="uf-kullanici-adi">{t("userFormModal.kullaniciAdi")} *</Label>
              <Input id="uf-kullanici-adi" value={kullaniciAdi} autoComplete="off" onChange={(e) => setKullaniciAdi(e.target.value)} disabled={kilitli} />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              {/* WAS (until #628): {olustur ? `${t("userFormModal.sifre")} *` : t("userFormModal.yeniSifre")} */}
              <Label for="uf-sifre">{olustur ? t("userFormModal.sifre") : t("userFormModal.yeniSifre")}</Label>
              <Input
                id="uf-sifre" type="password" autoComplete="new-password" value={sifre}
                placeholder={olustur ? undefined : t("userFormModal.degistirmekIcinDoldurun")}
                onChange={(e) => setSifre(e.target.value)} disabled={kilitli}
              />
              {olustur && <div className="form-text">{t("userFormModal.sifreIpucu")}</div>}
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label for="uf-ad">{t("userFormModal.ad")}</Label>
              <Input id="uf-ad" value={ad} onChange={(e) => setAd(e.target.value)} disabled={kilitli} />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label for="uf-soyad">{t("userFormModal.soyad")}</Label>
              <Input id="uf-soyad" value={soyad} onChange={(e) => setSoyad(e.target.value)} disabled={kilitli} />
            </FormGroup>
          </Col>
          <Col md="5">
            <FormGroup>
              <Label for="uf-unvan">{t("userFormModal.unvan")}</Label>
              <Input id="uf-unvan" value={unvan} onChange={(e) => setUnvan(e.target.value)} disabled={kilitli} />
            </FormGroup>
          </Col>
          <Col md="3">
            <FormGroup>
              <Label for="uf-uyruk">{t("userFormModal.uyruk")}</Label>
              <Input id="uf-uyruk" type="select" value={uyruk} onChange={(e) => setUyruk(e.target.value)} disabled={kilitli}>
                <option value="1">{t("userFormModal.yerli")}</option>
                <option value="2">{t("userFormModal.yabanci")}</option>
              </Input>
            </FormGroup>
          </Col>
          <Col md="4">
            <FormGroup>
              <Label for="uf-dil">{t("userFormModal.dil")}</Label>
              <Input
                id="uf-dil" type="select" value={dil} onChange={(e) => setDil(e.target.value)}
                disabled={kilitli || (!olustur && dilIlk === null)}
              >
                <option value="">{t("userFormModal.dilOtomatik")}</option>
                <option value="1">{t("userFormModal.dilTr")}</option>
                <option value="2">{t("userFormModal.dilEn")}</option>
              </Input>
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label for="uf-telefon">{t("userFormModal.telefon")}</Label>
              <TelefonGirdisi id="uf-telefon" value={telefon} onChange={setTelefon} disabled={kilitli} />
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label for="uf-eposta">{t("userFormModal.eposta")}</Label>
              <Input id="uf-eposta" value={eposta} inputMode="email" onChange={(e) => setEposta(e.target.value)} disabled={kilitli} />
            </FormGroup>
          </Col>
          {olustur ? (
            <Col md="12">
              <FormGroup>
                <Label for="uf-rol">{t("userFormModal.rol")} *</Label>
                {roller == null ? (
                  <div><Spinner size="sm" color="primary" /></div>
                ) : (
                  <Input id="uf-rol" type="select" value={rolId} onChange={(e) => setRolId(e.target.value)} disabled={kilitli}>
                    <option value="">{t("userFormModal.rolSec")}</option>
                    {/* WAS (until #628): {roller.map(...)} -- every role; now only the ones this login may give */}
                    {gorunenRoller.map((r) => <option key={r.id} value={r.id}>{r.ad}</option>)}
                  </Input>
                )}
              </FormGroup>
              {hedefRol?.staff && (
                <Alert fade={false} color="danger" className="small mt-3 mb-0">{t("rolSekmesi.personelOluyor")}</Alert>
              )}
              {firmaGerekir && (sabitFirma ? (
                <FormGroup>
                  <Label for="uf-firma">{t("userFormModal.firma")}</Label>
                  <Input id="uf-firma" value={sabitFirma.ad || `#${sabitFirma.id}`} readOnly disabled />
                </FormGroup>
              ) : (
                <div className="mb-3" data-testid="uf-firma-secici">
                  <FirmaSearchSelect
                    forceLive label={t("userFormModal.firma")} hint={t("userFormModal.firmaIpucu")}
                    value={firma} onChange={setFirma} disabled={kilitli}
                  />
                </div>
              ))}
              {portalGoster && (
                <FormGroup data-testid="uf-portal-roller">
                  <Label className="d-block">{t("userFormModal.portalRolleri")}</Label>
                  {portalRolleri === null ? (
                    <Spinner size="sm" color="primary" />
                  ) : (
                    <div className="d-flex flex-wrap gap-3">
                      {portalRolleri.map((r) => (
                        <FormGroup check key={r.id} className="mb-1">
                          <Input
                            id={`uf-portal-rol-${r.id}`} type="checkbox" checked={portalSecim.has(r.id)} disabled={kilitli}
                            onChange={(e) => setPortalSecim((x) => { const y = new Set(x); if (e.target.checked) y.add(r.id); else y.delete(r.id); return y; })}
                          />
                          <Label for={`uf-portal-rol-${r.id}`} check>{r.ad}</Label>
                        </FormGroup>
                      ))}
                    </div>
                  )}
                  <div className="form-text">{portalHata ? t("userFormModal.portalRolleriOkunamadi") : t("userFormModal.portalRolleriIpucu")}</div>
                </FormGroup>
              )}
              {olustur && sifreBos && epostaVar && (
                <FormGroup check className="mb-2">
                  <Input id="uf-posta" type="checkbox" checked={postaGonder} onChange={(e) => setPostaGonder(e.target.checked)} disabled={kilitli} />
                  <Label for="uf-posta" check>{t("userFormModal.aktivasyonMailiGonder")}</Label>
                </FormGroup>
              )}
              {olustur && sifreBos && !epostaVar && (
                <Alert fade={false} color="warning" className="small py-2 mb-0" data-testid="uf-giris-yapamaz">{t("userFormModal.girisYapamaz")}</Alert>
              )}
            </Col>
          ) : (
            <Col md="5">
              <FormGroup className="mb-0">
                <Label for="uf-durum">{t("userFormModal.durum")}</Label>
                <Input id="uf-durum" type="select" value={aktif} onChange={(e) => setAktif(e.target.value)} disabled={kilitli}>
                  <option value="1">{t("userFormModal.aktif")}</option>
                  <option value="0">{t("userFormModal.pasif")}</option>
                </Input>
              </FormGroup>
            </Col>
          )}
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={kapat} disabled={saving}>{olusan || bitis ? t("common:kapat") : t("common:iptal")}</Button>
        {!olusan && !bitis && (
          <Button color="primary" onClick={kaydet} disabled={saving}>
            {saving ? <Spinner size="sm" /> : (olustur ? t("userFormModal.olustur") : t("common:kaydet"))}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default UserFormModal;
